/**
 * DashboardPage Component - Phase 3H
 * BSL Compliant: Factual displays only.
 * 
 * Simplified layout with PoolStatusTable as primary component.
 */
import { PageContainer } from '../components/layout';
import { Card, CardContent } from '../components/common';
import { LoadingSpinner, EmptyState } from '../components/common';
import { PoolStatusTable, RecentActivityTable } from '../components/dashboard';
import { useCurrentUser } from '../hooks';
import { useMemberships } from '../hooks/useMemberships';
import { usePools } from '../hooks/usePools';
import { usePredictions } from '../hooks/usePredictions';
import { usePortfolios } from '../hooks/usePortfolios';
import { Prediction, Portfolio } from '../types';

interface Activity {
  id: string;
  type: 'prediction' | 'portfolio';
  pool_name: string;
  description: string;
  status: 'pending' | 'won' | 'lost' | 'cancelled';
  amount?: number;
  created_at: string;
}

function convertToActivities(
  predictions: Prediction[], 
  portfolios: Portfolio[], 
  poolsMap: Map<string, string>
): Activity[] {
  const predictionActivities: Activity[] = predictions.map(p => ({
    id: p.prediction_id,
    type: 'prediction' as const,
    pool_name: poolsMap.get(p.pool_id) || p.pool_id,
    description: p.predicted_outcome,
    status: p.status,
    amount: p.wager_amount,
    created_at: p.created_at,
  }));

  const portfolioActivities: Activity[] = portfolios.map(p => ({
    id: p.portfolio_id,
    type: 'portfolio' as const,
    pool_name: poolsMap.get(p.pool_id) || p.pool_id,
    description: `${p.allocations?.length ?? 0} allocations`,
    status: p.status === 'settled' || p.status === 'completed' ? 'won' : 
            p.status === 'cancelled' ? 'cancelled' : 'pending',
    amount: p.total_stake,
    created_at: p.created_at,
  }));

  return [...predictionActivities, ...portfolioActivities]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
}

export function DashboardPage() {
  const { userId } = useCurrentUser();
  const { pools, loading: poolsLoading } = usePools();
  const { memberships, loading: membershipsLoading } = useMemberships(userId);
  const { predictions, loading: predictionsLoading } = usePredictions(userId);
  const { portfolios, loading: portfoliosLoading } = usePortfolios(userId);

  const isLoading = poolsLoading || membershipsLoading;
  const isGuest = !userId || userId === 'guest';

  const poolsMap = new Map(pools.map(p => [p.pool_id, p.pool_name]));
  const activities = convertToActivities(predictions, portfolios, poolsMap);

  if (isGuest) {
    return (
      <PageContainer>
        <Card>
          <CardContent className="py-12">
            <EmptyState title="Welcome to North Star" description="Sign in to view your dashboard and start making predictions" />
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  if (isLoading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Dashboard</h1>
          <p className="text-slate-400 mt-1">Welcome back, {userId}</p>
        </div>

        {/* Primary: Pool Status Table */}
        <PoolStatusTable 
          memberships={memberships} 
          pools={pools} 
          loading={poolsLoading || membershipsLoading} 
        />

        {/* Secondary: Recent Activity */}
        <RecentActivityTable 
          activities={activities.slice(0, 10)} 
          loading={predictionsLoading || portfoliosLoading} 
        />
      </div>
    </PageContainer>
  );
}

export default DashboardPage;